import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { List } from "react-content-loader";
import Wizard, { useWizard } from "../wizard/Wizard/Wizard";
import WizardStep, {
  CustomStepInjectedProps,
} from "../wizard/WizardStep/WizardStep";
import WorkflowBreadcrumbs from "../common/breadcrumbs/WorkflowBreadcrumbs";
import BreadcrumbItem from "../common/breadcrumbs/BreadcrumbItem";
import { useHttpRequest, LoadingState } from "../http";
import createTeamStyle from "./createTeam.module.css";
import styles from "../common/loader.module.css";
import CreateTeamInputStep from "./inputStep/CreateTeamInputStep";
import CreateTeamReviewStep from "./reviewStep/CreateTeamReviewStep";
import { CreateTeamContext } from "./CreateTeamContext";
import { CreateTeamWorkflowResult } from "./CreateTeamWorkflowResult";
import useWorkflowAuthorizationCheck, {
  UseWorkflowAuthorizationHookResult,
} from "../common/authorization/workflowAuthorization";
import Banner from "../banner/Banner";

const BREADCRUMBS_TITLE = "New team";

const INPUT_STEP_TITLE = "Enter team details";
const REVIEW_STEP_TITLE = "Review and submit";

const steps: (keyof CreateTeamWorkflowResult)[] = ["input", "review"];

interface CreateTeamWorkflowProps {
  inputStep?: React.FC<CustomStepInjectedProps<{ text: string }>>;
  reviewStep?: React.FC<CustomStepInjectedProps<undefined>>;
  workflowContext: CreateTeamContext;
}
const CreateTeamWorkflow: React.FC<CreateTeamWorkflowProps> = ({
  workflowContext,
}): JSX.Element => {
  const [result, setResult] = useWizard<CreateTeamWorkflowResult>({
    input: {},
  });

  const history = useHistory();
  const { parentTeamId, metrics, displayBanner } = workflowContext;

  const {
    loadingState: isAuthorizedLoadingState,
    isAuthorized,
  }: UseWorkflowAuthorizationHookResult = useWorkflowAuthorizationCheck(
    parentTeamId,
    metrics
  );

  useEffect(() => {
    if (
      isAuthorizedLoadingState === LoadingState.NO_DATA ||
      isAuthorizedLoadingState === LoadingState.LOADING
    ) {
      return;
    }
    console.log(`Authorization check request finished. AuthZ: ${isAuthorized}`);
    if (!isAuthorized) {
      throw new Error(
        "You are not an admin of this team. You must be a team admin to create subteams."
      );
    }
  }, [isAuthorized, isAuthorizedLoadingState]);

  // retrieve the breadcrumbs for the parent of the future team.
  const {
    loadingState: targetLoadingState,
    data: response,
    error: targetFetchError,
  } = useHttpRequest("createTeamGetBreadcrumbs", metrics, {
    url: `/api/v1/teams/${parentTeamId}/ancestors?includeCurrentTeam=true`,
    method: "GET",
  });

  if (targetFetchError) {
    if (targetFetchError.response && targetFetchError.response.status === 404) {
      throw new Error(
        `Unable to resolve parent team with id: ${parentTeamId}.  Redirecting to home page.`
      );
    } else {
      throw targetFetchError;
    }
  }

  const ancestors =
    targetLoadingState === LoadingState.RESOLVED
      ? (response as any).data
      : undefined;

  if (!ancestors) {
    return (
      <div className={createTeamStyle["loading-holder"]}>
        <List />
      </div>
    );
  }

  const breadcrumbItems: BreadcrumbItem[] = ancestors.map(
    (ancestor: { name: string }) => ({ name: ancestor.name })
  );
  return (
    <div>
      <WorkflowBreadcrumbs
        breadcrumbItems={breadcrumbItems}
        workflowTitle={`${BREADCRUMBS_TITLE}`}
      />
      {displayBanner && <Banner />}
      <Wizard<CreateTeamWorkflowResult, CreateTeamContext>
        result={result}
        updateResult={setResult}
        steps={steps}
        workflowContext={workflowContext}
        onConfirmCancel={() => {
          history.push(`/team/${parentTeamId}`);
        }}
      >
        <WizardStep<CreateTeamWorkflowResult, "input">
          stepId="input"
          stepTitle={`${INPUT_STEP_TITLE}`}
          component={CreateTeamInputStep}
          preventUnmount
        />
        <WizardStep<CreateTeamWorkflowResult, "review">
          stepId="review"
          stepTitle={`${REVIEW_STEP_TITLE}`}
          component={CreateTeamReviewStep}
        />
      </Wizard>
    </div>
  );
};

export default CreateTeamWorkflow;
